"use client"

import { useId, useState, useMemo, useEffect } from "react"
import { CheckIcon, ChevronDownIcon } from "lucide-react"
import { useI18n } from '../hooks/useI18n';

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"

interface ProductOption {
    value: string;
    label: string;
}

interface ProductSearchSelectProps {
    products: ProductOption[];
    value: string;
    onChange: (productId: string) => void;
    placeholder?: string;
    label?: string;
}

export default function ProductSearchSelect({
    products,
    value,
    onChange,
    placeholder = "Select product",
    label,
}: ProductSearchSelectProps) {
    const { t } = useI18n();
    const id = useId()
    const [open, setOpen] = useState<boolean>(false)
    const [search, setSearch] = useState<string>("")

    // Memoize the selected product to avoid O(n) lookup on every render
    const selectedProduct = useMemo(
        () => products.find(p => p.value === value),
        [products, value]
    );

    // Reset scroll position when search changes or dropdown opens
    useEffect(() => {
        if (open) {
            // Use a small delay to ensure the DOM is updated
            const timer = setTimeout(() => {
                const scrollContainer = document.querySelector('[cmdk-list]')
                if (scrollContainer) {
                    scrollContainer.scrollTop = 0
                }
            }, 5)
            return () => clearTimeout(timer)
        }
    }, [open, search]);

    // Reset search when dropdown closes
    useEffect(() => {
        if (!open) {
            setSearch("")
        }
    }, [open]);

    return (
        <div className="*:not-first:mt-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="bg-background hover:bg-background border-input w-full justify-between px-3 font-normal outline-offset-0 outline-none focus-visible:outline-[3px]"
                    >
                        <span className={cn("truncate", !value && "text-muted-foreground")}>
                            {selectedProduct ? selectedProduct.label : placeholder}
                        </span>
                        <ChevronDownIcon
                            size={16}
                            className="text-muted-foreground/80 shrink-0"
                            aria-hidden="true"
                        />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="border-input w-full min-w-[var(--radix-popper-anchor-width)] p-0"
                    align="start"
                >
                    <Command>
                        <CommandInput
                            placeholder={t('actions.search', 'common')}
                            value={search}
                            onValueChange={setSearch}
                        />
                        <CommandList>
                            <CommandEmpty>{t('products.noProductFound', 'common')}</CommandEmpty>
                            <CommandGroup>
                                {products.map((product) => (
                                    <CommandItem
                                        key={product.value}
                                        value={product.label}
                                        onSelect={() => {
                                            onChange(product.value === value ? "" : product.value)
                                            setOpen(false)
                                        }}
                                    >
                                        {product.label}
                                        {value === product.value && (
                                            <CheckIcon size={16} className="ml-auto" />
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    )
}
